<template>
  <div>
    <v-card id="calendar-actions">
      <v-card-text class="d-flex align-center flex-wrap pb-0 pt-sm-0">
        <div class="d-flex align-center flex-wrap pb-0">
          <v-btn
            outlined
            class="mr-3"
            color="grey darken-2"
            @click="setToday"
          >
            Today
          </v-btn>
          <div>
            <v-btn
              icon
              class="ma-2"
              @click="$refs.calendar.prev()"
            >
              <v-icon
                size="30"
                class="me-1"
              >
                {{ icons.mdiChevronLeft }}
              </v-icon>
            </v-btn>
            <v-btn
              icon
              class="ma-2"
              @click="$refs.calendar.next()"
            >
              <v-icon
                size="30"
                class="me-1"
              >
                {{ icons.mdiChevronRight }}
              </v-icon>
            </v-btn>
            <span>{{ $refs.calendar ? $refs.calendar.title : '' }}</span>
          </div>
        </div>
        <v-spacer v-if="$vuetify.breakpoint.smAndUp"></v-spacer>
        <div class="row text-right align-center pb-6 pb-sm-0">
          <div
            v-if="userRole === 'Clerk' || userRole === 'SPD reporter'"
            class="col-sm-5 col-12 d-flex justify-center"
          >
            <span v-if="accounts.length > 0 && (userRole === 'Clerk' || userRole === 'SPD reporter')">
              Assigned to: {{ userBranch ? userBranch.name : 'N/A' }}
            </span>
          </div>
          <v-spacer v-else-if="$vuetify.breakpoint.smAndUp"></v-spacer>
          <div
            v-if="can('write', 'Event')"
            class="col-sm-3 col-12"
          >
            <!-- branch -->
            <v-btn
              color="primary"
              block
              class="me-3 receipt-button"
              @click="isBranchDialogVisible = true; fetchClerks(); populateBranchUsers(branches)"
            >
              <span>{{ t('Branches') }}</span>
            </v-btn>
          </div>
          <div
            v-if="can('write', 'Event')"
            class="col-sm-4 col-12"
          >
            <!-- create leave -->
            <v-btn
              v-if="can('write', 'Event') && (userRole === 'Clerk' || userRole === 'SPD reporter' || (userRole === 'Part timer' && userData.apply_leave))"
              color="primary"
              block
              class="me-3 receipt-button"
              @click="event.event_type = 'Leave'; isLeaveDialogVisible = true; fetchClerks()"
            >
              <v-icon
                size="18"
                class="me-1"
              >
                {{ icons.mdiPlus }}
              </v-icon>
              <span>{{ t('Create Leave') }}</span>
            </v-btn>
            <template v-if="can('write', 'Event') && userRole === 'Secretary'">
              <!-- create event/announcement -->
              <v-btn
                color="primary"
                block
                class="me-3 receipt-button"
                @click="event.event_type = 'Event'; isEventDialogVisible = true"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiPlus }}
                </v-icon>
                <span>{{ t('Create Event') }}</span>
              </v-btn>
            </template>
            <template v-else-if="can('write', 'Event') && (userRole === 'Auditor')">
              <!-- add blocked days -->
              <v-btn
                color="primary"
                block
                class="me-3 receipt-button"
                @click="event.event_type = 'Blocked'; isBlockDialogVisible = true"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiPlus }}
                </v-icon>
                <span>{{ t('Block Days') }}</span>
              </v-btn>
            </template>
          </div>
        </div>
      </v-card-text>
    </v-card>
    <v-calendar
      id="calendar"
      ref="calendar"
      v-model="focus"
      :weekdays="weekday"
      :type="type"
      :events="events"
      :event-overlap-mode="mode"
      :event-overlap-threshold="30"
      @change="fetchEvents"
    >
      <template v-slot:day="{ date }">
        <div :class="{ 'blocked-day': isBlocked(date) }"></div>
      </template>
      <template v-slot:event="{ event }">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
              class="pl-1 cursor-pointer"
              @click="eventClicked(event.event)"
            >
              {{ event.name }}
            </div>
          </template>
          <span>{{ event.name }}</span>
        </v-tooltip>
      </template>
    </v-calendar>
    <div>
      <v-card
        v-if="userRole === 'Clerk' || userRole === 'SPD reporter'"
        id="invite-list"
        class="mt-5"
      >
        <v-card-text class="text-h6">
          Invites
        </v-card-text>

        <v-divider></v-divider>

        <v-card-text class="pt-5">
          <v-row>
            <v-col
              lg="2"
              cols="3"
            >
              <div class="d-flex align-center">
                <v-select
                  v-model="inviteTablePagination.per"
                  :items="inviteListLengthOptions"
                  single-line
                  outlined
                  dense
                  hide-details
                  class="invite-list-row-selection"
                  @input="inviteListLengthChange()"
                ></v-select>
                <div class="pl-3">
                  {{ t('Rows') }}
                </div>
              </div>
            </v-col>

            <v-col
              lg="10"
              cols="9"
              class="d-flex justify-end"
            >
              <v-pagination
                v-model="inviteTablePagination.current_page"
                total-visible="6"
                :length="inviteTablePagination.total_page"
                class="list-row-selection"
                @input="invitePageChange()"
              ></v-pagination>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- table -->
        <v-data-table
          :headers="inviteTableColumns"
          :items="invites"
          :items-per-page="100"
          :loading="fetchLoading"
          mobile-breakpoint="0"
          hide-default-footer
          :disable-sort="true"
          class="text-no-wrap"
        >
          <!-- Actions -->
          <template #[`item.actions`]="{item}">
            <div class="d-flex align-center">
              <!-- View Invite -->
              <v-tooltip
                bottom
              >
                <template #activator="{ on, attrs }">
                  <div
                    class="d-inline-block"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-btn
                      icon
                      small
                      @click="isViewEventDialogVisible = true; editEvent = item"
                    >
                      <v-icon size="18">
                        {{ icons.mdiEyeOutline }}
                      </v-icon>
                    </v-btn>
                  </div>
                </template>
                <span>View</span>
              </v-tooltip>
            </div>
          </template>
          <!-- ID -->
          <template #[`item.id`]="{item}">
            {{ item.id }}
          </template>

          <!-- Date Range -->
          <template #[`item.date_range`]="{item}">
            <span v-if="item.end_date_formatted !== item.start_date_formatted">
              {{ item.start_date_formatted }} - {{ item.end_date_formatted }}
            </span>
            <span v-else>
              {{ item.start_date_formatted }}
            </span>
          </template>
        </v-data-table>

        <v-card-text class="pt-5">
          <v-row>
            <v-col
              lg="2"
              cols="3"
            >
              <div class="d-flex align-center">
                <v-select
                  v-model="inviteTablePagination.per"
                  :items="inviteListLengthOptions"
                  single-line
                  outlined
                  dense
                  hide-details
                  class="invite-list-row-selection"
                  @input="inviteListLengthChange()"
                ></v-select>
                <div class="pl-3">
                  {{ t('Rows') }}
                </div>
              </div>
            </v-col>

            <v-col
              lg="10"
              cols="9"
              class="d-flex justify-end"
            >
              <v-pagination
                v-model="inviteTablePagination.current_page"
                total-visible="6"
                :length="inviteTablePagination.total_page"
                class="list-row-selection"
                @input="invitePageChange()"
              ></v-pagination>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card
        v-if="userRole === 'Clerk' || userRole === 'SPD reporter' || userRole === 'Auditor' || userRole === 'Secretary'"
        id="request-list"
        class="mt-5"
      >
        <v-card-text class="text-h6">
          Requests
        </v-card-text>

        <v-divider></v-divider>

        <v-card-text class="pt-5">
          <v-row>
            <v-col
              lg="2"
              cols="3"
            >
              <div class="d-flex align-center">
                <v-select
                  v-model="requestTablePagination.per"
                  :items="requestListLengthOptions"
                  single-line
                  outlined
                  dense
                  hide-details
                  class="request-list-row-selection"
                  @input="requestListLengthChange()"
                ></v-select>
                <div class="pl-3">
                  {{ t('Rows') }}
                </div>
              </div>
            </v-col>

            <v-col
              lg="10"
              cols="9"
              class="d-flex justify-end"
            >
              <v-pagination
                v-model="requestTablePagination.current_page"
                total-visible="6"
                :length="requestTablePagination.total_page"
                class="list-row-selection"
                @input="requestPageChange()"
              ></v-pagination>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- table -->
        <v-data-table
          :headers="requestTableColumns"
          :items="requests"
          :items-per-page="100"
          :loading="fetchLoading"
          mobile-breakpoint="0"
          hide-default-footer
          :disable-sort="true"
          class="text-no-wrap"
        >
          <!-- Actions -->
          <template #[`item.actions`]="{item}">
            <div class="d-flex align-center">
              <!-- View Request -->
              <v-tooltip
                bottom
              >
                <template #activator="{ on, attrs }">
                  <div
                    class="d-inline-block"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-btn
                      icon
                      small
                      @click="isViewRequestDialogVisible = true; editRequest = item;"
                    >
                      <v-icon size="18">
                        {{ icons.mdiEyeOutline }}
                      </v-icon>
                    </v-btn>
                  </div>
                </template>
                <span>View</span>
              </v-tooltip>
            </div>
          </template>
          <!-- ID -->
          <template #[`item.id`]="{item}">
            {{ item.id }}
          </template>

          <!-- Date Range -->
          <template #[`item.date_range`]="{item}">
            <span v-if="item.end_date_formatted !== item.start_date_formatted">
              {{ item.start_date_formatted }} - {{ item.end_date_formatted }}
            </span>
            <span v-else>
              {{ item.start_date_formatted }}
            </span>
          </template>
        </v-data-table>

        <v-card-text class="pt-5">
          <v-row>
            <v-col
              lg="2"
              cols="3"
            >
              <div class="d-flex align-center">
                <v-select
                  v-model="requestTablePagination.per"
                  :items="requestListLengthOptions"
                  single-line
                  outlined
                  dense
                  hide-details
                  class="request-list-row-selection"
                  @input="requestListLengthChange()"
                ></v-select>
                <div class="pl-3">
                  {{ t('Rows') }}
                </div>
              </div>
            </v-col>

            <v-col
              lg="10"
              cols="9"
              class="d-flex justify-end"
            >
              <v-pagination
                v-model="requestTablePagination.current_page"
                total-visible="6"
                :length="requestTablePagination.total_page"
                class="list-row-selection"
                @input="requestPageChange()"
              ></v-pagination>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card
        v-if="userRole === 'Auditor'"
        id="block-list"
        class="mt-5"
      >
        <v-card-text class="text-h6">
          Blocked dates
        </v-card-text>

        <v-divider></v-divider>

        <v-card-text class="pt-5">
          <v-row>
            <v-col
              lg="2"
              cols="3"
            >
              <div class="d-flex align-center">
                <v-select
                  v-model="blockTablePagination.per"
                  :items="blockListLengthOptions"
                  single-line
                  outlined
                  dense
                  hide-details
                  class="block-list-row-selection"
                  @input="blockListLengthChange()"
                ></v-select>
                <div class="pl-3">
                  {{ t('Rows') }}
                </div>
              </div>
            </v-col>

            <v-col
              lg="10"
              cols="9"
              class="d-flex justify-end"
            >
              <v-pagination
                v-model="blockTablePagination.current_page"
                total-visible="6"
                :length="blockTablePagination.total_page"
                class="list-row-selection"
                @input="blockPageChange()"
              ></v-pagination>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- table -->
        <v-data-table
          :headers="blockTableColumns"
          :items="blockedDatesList"
          :items-per-page="100"
          :loading="fetchLoading"
          mobile-breakpoint="0"
          hide-default-footer
          :disable-sort="true"
          class="text-no-wrap"
        >
          <!-- Actions -->
          <template #[`item.actions`]="{item}">
            <div class="d-flex align-center">
              <!-- View Date -->
              <v-tooltip
                bottom
              >
                <template #activator="{ on, attrs }">
                  <div
                    class="d-inline-block"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-btn
                      icon
                      small
                      @click="isViewBlockedDialogVisible = true; editRequest = item;"
                    >
                      <v-icon size="18">
                        {{ icons.mdiEyeOutline }}
                      </v-icon>
                    </v-btn>
                  </div>
                </template>
                <span>View</span>
              </v-tooltip>
            </div>
          </template>
          <!-- Description -->
          <template #[`item.remarks`]="{item}">
            {{ cleanRemarks(item.remarks) }}
          </template>

          <!-- Date Range -->
          <template #[`item.date_range`]="{item}">
            <span v-if="item.end_date_formatted !== item.start_date_formatted">
              {{ item.start_date_formatted }} - {{ item.end_date_formatted }}
            </span>
            <span v-else>
              {{ item.start_date_formatted }}
            </span>
          </template>
        </v-data-table>

        <v-card-text class="pt-5">
          <v-row>
            <v-col
              lg="2"
              cols="3"
            >
              <div class="d-flex align-center">
                <v-select
                  v-model="blockTablePagination.per"
                  :items="blockListLengthOptions"
                  single-line
                  outlined
                  dense
                  hide-details
                  class="block-list-row-selection"
                  @input="blockListLengthChange()"
                ></v-select>
                <div class="pl-3">
                  {{ t('Rows') }}
                </div>
              </div>
            </v-col>

            <v-col
              lg="10"
              cols="9"
              class="d-flex justify-end"
            >
              <v-pagination
                v-model="blockTablePagination.current_page"
                total-visible="6"
                :length="blockTablePagination.total_page"
                class="list-row-selection"
                @input="blockPageChange()"
              ></v-pagination>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card
        v-if="userRole === 'Auditor' || userRole === 'Secretary'"
        class="mt-5"
      >
        <v-card-text>
          <v-row class="d-flex align-center">
            <v-col
              cols="12"
              md="6"
            >
              <v-card-title class="pa-0 ma-0">Leave summary for {{ summaryDateRangeText }}</v-card-title>
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-btn
                color="primary"
                block
                class="receipt-button"
                @click="isBalanceDialogVisible = true; fetchBalances()"
              >
                <span>View leave balances</span>
              </v-btn>
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <!-- Date range -->
              <v-menu
                v-model="isSummaryDateMenuOpen"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :append-icon="icons.mdiCalendar"
                    :value="summaryDateRangeText"
                    readonly
                    dense
                    outlined
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="summaryDateRange"
                  color="primary"
                  :first-day-of-week="1"
                  range
                >
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    block
                    @click="summaryDateRangeChange()"
                  >
                    {{ t('Set Dates') }}
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-text>
          <v-row class="py-3">
            <v-col
              cols="12"
              sm="6"
              lg="4"
              class="px-5 py-0"
              :class="{
                'dark-divider': $vuetify.theme.dark && $vuetify.breakpoint.mdAndUp,
                'light-divider': !$vuetify.theme.dark && $vuetify.breakpoint.mdAndUp
              }"
            >
              <v-card-title
                class="align-start d-flex align-center pl-0"
                style="font-size: 16px;"
              >
                <p class="d-flex align-center mb-0">
                  Total Leaves
                </p>
              </v-card-title>

              <LeaveSummaryBreakdown
                v-for="leaveType in ['Annual leave', 'Emergency leave', 'Sick leave', 'Maternity leave', 'Compassionate leave', 'Paternity leave', 'Unpaid leave']"
                :key="leaveType"
                :leave-type="leaveType"
                :summary="leaveSummary.total_leaves"
                class="font-weight-semibold"
              />
            </v-col>
            <v-col
              v-for="(leaves, user) in leaveSummary.leaves"
              :key="user"
              cols="12"
              sm="6"
              lg="4"
              class="px-5 py-0"
              :class="{
                'dark-divider': $vuetify.theme.dark && $vuetify.breakpoint.mdAndUp,
                'light-divider': !$vuetify.theme.dark && $vuetify.breakpoint.mdAndUp
              }"
            >
              <v-card-title
                class="align-start d-flex align-center pl-0"
                style="font-size: 16px;"
              >
                <p class="d-flex align-center mb-0">
                  Leaves by {{ user }}
                </p>
              </v-card-title>

              <LeaveSummaryBreakdown
                v-for="leaveType in ['Annual leave', 'Emergency leave', 'Sick leave', 'Maternity leave', 'Compassionate leave', 'Paternity leave', 'Unpaid leave']"
                :key="leaveType"
                :leave-type="leaveType"
                :summary="leaves"
                class="font-weight-semibold"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card
        id="log-list"
        class="mt-5"
      >
        <v-card-text class="text-h6">
          Logs
        </v-card-text>

        <v-divider></v-divider>

        <v-card-text class="pt-5">
          <v-row>
            <v-col
              lg="2"
              cols="3"
            >
              <div class="d-flex align-center">
                <v-select
                  v-model="logTablePagination.per"
                  :items="logListLengthOptions"
                  single-line
                  outlined
                  dense
                  hide-details
                  class="log-list-row-selection"
                  @input="logListLengthChange()"
                ></v-select>
                <div class="pl-3">
                  {{ t('Rows') }}
                </div>
              </div>
            </v-col>

            <v-col
              lg="10"
              cols="9"
              class="d-flex justify-end"
            >
              <v-pagination
                v-model="logTablePagination.current_page"
                total-visible="6"
                :length="logTablePagination.total_page"
                class="list-row-selection"
                @input="logPageChange()"
              ></v-pagination>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- table -->
        <v-data-table
          :headers="logTableColumns"
          :items="logs"
          :items-per-page="100"
          :loading="fetchLoading"
          mobile-breakpoint="0"
          hide-default-footer
          :disable-sort="true"
          class="text-no-wrap"
        ></v-data-table>

        <v-card-text class="pt-5">
          <v-row>
            <v-col
              lg="2"
              cols="3"
            >
              <div class="d-flex align-center">
                <v-select
                  v-model="logTablePagination.per"
                  :items="logListLengthOptions"
                  single-line
                  outlined
                  dense
                  hide-details
                  class="log-list-row-selection"
                  @input="logListLengthChange()"
                ></v-select>
                <div class="pl-3">
                  {{ t('Rows') }}
                </div>
              </div>
            </v-col>

            <v-col
              lg="10"
              cols="9"
              class="d-flex justify-end"
            >
              <v-pagination
                v-model="logTablePagination.current_page"
                total-visible="6"
                :length="logTablePagination.total_page"
                class="list-row-selection"
                @input="logPageChange()"
              ></v-pagination>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>
    <v-dialog
      v-model="isLeaveDialogVisible"
      width="500"
      @input="resetEvent()"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          Create New Leave
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isLeaveDialogVisible = false; resetEvent()"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1">
              <v-row>
                <!-- Half day -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Half day:
                  </p>
                  <v-select
                    v-model="isHalfDay"
                    :items="[{ id: true, name: 'Yes' }, { id: false, name: 'No' }]"
                    item-value="id"
                    item-text="name"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Half day?"
                  ></v-select>
                </v-col>

                <!-- Half day period -->
                <v-col
                  v-if="isHalfDay"
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Half day period:
                  </p>
                  <v-select
                    v-model="event.half_day_period"
                    :items="['Morning', 'Afternoon']"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Select Period"
                  ></v-select>
                </v-col>

                <!-- Event Date -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    {{ event.event_type }} Date Range:
                  </p>
                  <!-- Date range -->
                  <v-menu
                    v-model="isDateMenuOpen"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :append-icon="icons.mdiCalendar"
                        :value="dateRangeText"
                        readonly
                        dense
                        outlined
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dateRange"
                      color="primary"
                      :min="new Date().toISOString().substring(0, 10)"
                      :first-day-of-week="1"
                      :range="!isHalfDay"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        block
                        @click="dateRangeChange()"
                      >
                        {{ t('Set Dates') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Leave Type -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Leave Type:
                  </p>
                  <v-select
                    v-model="event.leave_type"
                    :items="leaveTypeOptions"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Select Leave Type"
                    @change="event.request_replacement_by_id = null"
                  ></v-select>
                </v-col>

                <!-- Leave Duration -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    No. of days:
                  </p>
                  <v-text-field
                    v-model="event.duration"
                    outlined
                    dense
                    min="1"
                    type="number"
                    hide-details="auto"
                    disabled
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>

                <!-- Medical Certificate -->
                <v-col
                  v-if="event.leave_type === 'Sick leave' || event.leave_type === 'Maternity leave'"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Medical Certificate:
                  </p>
                  <v-file-input
                    prepend-icon=""
                    :append-icon="icons.mdiPaperclip"
                    accept="application/pdf, image/png, image/jpg, image/jpeg"
                    show-size
                    dense
                    outlined
                    :rules="mcUploadRules"
                    label="Upload MC"
                    hide-details="auto"
                    @change="directUploadMC($event)"
                  >
                    <template v-slot:selection="{ text }">
                      <v-chip
                        small
                        label
                        color="primary"
                      >
                        {{ text }}
                      </v-chip>
                    </template>

                    <template v-slot:append>
                      <v-progress-circular
                        v-if="uploadLoading"
                        color="primary"
                        indeterminate
                        size="25"
                      ></v-progress-circular>
                    </template>
                  </v-file-input>
                </v-col>
                <v-col
                  v-if="(event.leave_type === 'Annual leave' && userBranch) || (soloClerkReplacement.includes(userData.id))"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Replacement By:
                  </p>
                  <!-- Request replacement by -->
                  <v-select
                    v-model="event.request_replacement_by_id"
                    :items="[{ name: 'No replacement', id: null }, ...accounts.filter(acc => acc.id !== userData.id &&
                      acc.branch && acc.branch.id !== userBranch.id && !removeReplacementUsers.includes(acc.id))
                    ]"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Request replacement by"
                  ></v-select>
                </v-col>

                <!-- Remarks -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Remarks:
                  </p>
                  <vue-editor
                    v-model="event.remarks"
                    placeholder="Remarks, if any"
                    :editorOptions="editorOptions"
                    spellcheck="false"
                    style="width: 100%;"
                  />
                  <!-- <v-textarea
                    v-model="event.remarks"
                    placeholder="Remarks, if any"
                    dense
                    outlined
                    rows="3"
                    hide-details
                  ></v-textarea> -->
                </v-col>
                <v-col
                  v-if="errors.length != 0"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <Alert
                    :messages="errors"
                    color="error"
                  ></Alert>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="mt-2">
          <v-btn
            color="primary"
            block
            :loading="saveLoading"
            :disabled="saveLoading || (!event.request_replacement_by_id && soloClerkReplacement.includes(userData.id))"
            @click="isConfirmDialogVisible = true"
          >
            Create leave request
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="isEventDialogVisible"
      width="500"
      @input="resetEvent()"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          Create New {{ event.event_type }}
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isEventDialogVisible = false; resetEvent()"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1">
              <v-row>
                <!-- Half day -->
                <v-col
                  v-if="event.event_type === 'Leave' || event.event_type === 'Leave on behalf' || event.event_type === 'Rotation'"
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Half day:
                  </p>
                  <v-select
                    v-model="isHalfDay"
                    :items="[{ id: true, name: 'Yes' }, { id: false, name: 'No' }]"
                    item-value="id"
                    item-text="name"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Half day?"
                  ></v-select>
                </v-col>

                <!-- Half day period -->
                <v-col
                  v-if="isHalfDay && (event.event_type === 'Leave' || event.event_type === 'Leave on behalf' || event.event_type === 'Rotation')"
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Half day period:
                  </p>
                  <v-select
                    v-model="event.half_day_period"
                    :items="['Morning', 'Afternoon']"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Select Period"
                  ></v-select>
                </v-col>

                <!-- Event Date -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    {{ event.event_type }} Date Range:
                  </p>
                  <!-- Date range -->
                  <v-menu
                    v-model="isDateMenuOpen"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :append-icon="icons.mdiCalendar"
                        :value="dateRangeText"
                        readonly
                        dense
                        outlined
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dateRange"
                      color="primary"
                      :min="new Date().toISOString().substring(0, 10)"
                      :first-day-of-week="1"
                      :range="!isHalfDay"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        block
                        @click="dateRangeChange()"
                      >
                        {{ t('Set Dates') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Event Type -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Event Type:
                  </p>
                  <v-select
                    v-model="event.event_type"
                    :items="['Event', 'Announcement', 'Leave', 'Rotation', 'Leave on behalf']"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Select Event Type"
                    @change="eventTypeChange"
                  ></v-select>
                </v-col>

                <!-- Event Duration -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    No. of days:
                  </p>
                  <v-text-field
                    v-model="event.duration"
                    outlined
                    dense
                    min="1"
                    type="number"
                    hide-details="auto"
                    disabled
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>

                <!-- Requested by -->
                <v-col
                  v-if="event.event_type === 'Leave on behalf'"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Requested by:
                  </p>
                  <v-select
                    v-model="event.requested_by_id"
                    :items="accounts"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Requested by"
                    @change="getUserBranch"
                  ></v-select>
                </v-col>

                <!-- Leave Type -->
                <v-col
                  v-if="event.event_type === 'Leave' || event.event_type === 'Leave on behalf'"
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Leave Type:
                  </p>
                  <v-select
                    v-model="event.leave_type"
                    :items="leaveTypeOptions"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Select Leave Type"
                    @change="event.request_replacement_by_id = null"
                  ></v-select>
                </v-col>

                <!-- Medical Certificate -->
                <v-col
                  v-if="event.leave_type === 'Sick leave' || event.leave_type === 'Maternity leave'"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Medical Certificate:
                  </p>
                  <v-file-input
                    prepend-icon=""
                    :append-icon="icons.mdiPaperclip"
                    accept="application/pdf, image/png, image/jpg, image/jpeg"
                    show-size
                    dense
                    outlined
                    :rules="mcUploadRules"
                    label="Upload MC"
                    hide-details="auto"
                    @change="directUploadMC($event)"
                  >
                    <template v-slot:selection="{ text }">
                      <v-chip
                        small
                        label
                        color="primary"
                      >
                        {{ text }}
                      </v-chip>
                    </template>

                    <template v-slot:append>
                      <v-progress-circular
                        v-if="uploadLoading"
                        color="primary"
                        indeterminate
                        size="25"
                      ></v-progress-circular>
                    </template>
                  </v-file-input>
                </v-col>

                <v-col
                  v-if="(event.event_type === 'Leave on behalf' && event.leave_type === 'Annual leave' && userBranch) || soloClerkReplacement.includes(event.requested_by_id)"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Replacement By:
                  </p>
                  <!-- Request replacement by -->
                  <v-select
                    v-model="event.request_replacement_by_id"
                    :items="[{ name: 'No replacement', id: null }, ...accounts.filter(acc => acc.id !== userData.id && acc.branch && acc.branch.id !== userBranch.id)]"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Request replacement by"
                  ></v-select>
                </v-col>

                <!-- Rotation By -->
                <v-col
                  v-if="event.event_type === 'Rotation'"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Rotation By:
                  </p>
                  <v-select
                    v-model="event.rotation_by_id"
                    :items="accounts"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Rotation by"
                    @change="fillOriginalBranch"
                  ></v-select>
                </v-col>

                <!-- Original branch -->
                <v-col
                  v-if="event.event_type === 'Rotation'"
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Original branch:
                  </p>
                  <v-select
                    v-model="event.original_branch_id"
                    :items="branches"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Select original branch"
                  ></v-select>
                </v-col>

                <!-- New branch -->
                <v-col
                  v-if="event.event_type === 'Rotation'"
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    New branch:
                  </p>
                  <v-select
                    v-model="event.new_branch_id"
                    :items="branches"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Select new branch"
                  ></v-select>
                </v-col>

                <!-- Description -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Remarks:
                  </p>
                  <vue-editor
                    v-model="event.remarks"
                    placeholder="Remarks"
                    :editorOptions="editorOptions"
                    spellcheck="false"
                    style="width: 100%;"
                  />
                  <!-- <v-textarea
                    v-model="event.remarks"
                    :placeholder="`${event.event_type} description`"
                    dense
                    outlined
                    rows="3"
                    hide-details
                  ></v-textarea> -->
                </v-col>

                <v-col
                  v-if="errors.length != 0"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <Alert
                    :messages="errors"
                    color="error"
                  ></Alert>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="mt-2">
          <v-btn
            color="primary"
            block
            :loading="saveLoading"
            :disabled="saveLoading || (!event.request_replacement_by_id && soloClerkReplacement.includes(event.requested_by_id))"
            @click="saveEvent()"
          >
            Create {{ event.event_type }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="isConfirmDialogVisible"
      width="500"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          Confirm leave?
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isConfirmDialogVisible = false"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <p>
            Confirm Leave Application {{ (confirmLessDur ? 'on ' : 'from ') + dateRangeText }}
          </p>
          <p class="mb-0">
            This is not reversible. Any wrong input in the application will still be deducted from your total AL
          </p>
        </v-card-text>

        <v-card-actions class="mt-2">
          <v-btn
            color="primary"
            block
            :loading="saveLoading"
            :disabled="saveLoading"
            @click="saveEvent()"
          >
            Yes, confirm leave
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="isBlockDialogVisible"
      width="500"
      @input="resetEvent()"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          Block days
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isBlockDialogVisible = false; resetEvent()"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1">
              <v-row>
                <!-- Event Date -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    {{ event.event_type }} Date Range:
                  </p>
                  <!-- Date range -->
                  <v-menu
                    v-model="isDateMenuOpen"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :append-icon="icons.mdiCalendar"
                        :value="dateRangeText"
                        readonly
                        dense
                        outlined
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dateRange"
                      color="primary"
                      :min="new Date().toISOString().substring(0, 10)"
                      :first-day-of-week="1"
                      range
                      :allowed-dates="allowedDates"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        block
                        @click="dateRangeChange()"
                      >
                        {{ t('Set Dates') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Event Duration -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    No. of days:
                  </p>
                  <v-text-field
                    v-model="event.duration"
                    outlined
                    dense
                    min="1"
                    type="number"
                    hide-details="auto"
                    disabled
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>

                <!-- Description -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Description
                  </p>
                  <vue-editor
                    v-model="event.remarks"
                    placeholder="Description"
                    :editorOptions="editorOptions"
                    spellcheck="false"
                    style="width: 100%;"
                  />
                  <!-- <v-textarea
                    v-model="event.remarks"
                    placeholder="Description"
                    dense
                    outlined
                    rows="3"
                    hide-details
                  ></v-textarea> -->
                </v-col>

                <v-col
                  v-if="errors.length != 0"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <Alert
                    :messages="errors"
                    color="error"
                  ></Alert>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="mt-2">
          <v-btn
            color="primary"
            block
            :loading="saveLoading"
            :disabled="saveLoading"
            @click="saveEvent()"
          >
            Block selected dates
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="isViewCompanyEventDialogVisible"
      width="500"
      @input="resetEditRequest()"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          {{ editRequest.event_type }}
          <div
            ref="lottieEvent"
            :data-type="editRequest.event_type"
            :style="{
              height: '50px',
              marginLeft: editRequest.event_type === 'Event' ? '-25px' : '0px'
            }"
          ></div>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isViewCompanyEventDialogVisible = false; resetEditRequest()"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1">
              <v-row>
                <!-- Event Date -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    {{ editRequest.event_type }} Date Range:
                  </p>
                  <!-- Date range -->
                  <v-menu
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :append-icon="icons.mdiCalendar"
                        :value="editRequestDateRangeText"
                        readonly
                        dense
                        outlined
                        hide-details
                        disabled
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="editRequestDateRange"
                      color="primary"
                      :min="new Date().toISOString().substring(0, 10)"
                      :first-day-of-week="1"
                      range
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        block
                      >
                        {{ t('Set Dates') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Event Type -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Type:
                  </p>
                  <v-select
                    v-model="editRequest.event_type"
                    :items="['Event', 'Announcement']"
                    dense
                    outlined
                    hide-details="auto"
                    disabled
                    placeholder="Select Event Type"
                  ></v-select>
                </v-col>

                <!-- Event Duration -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    No. of days:
                  </p>
                  <v-text-field
                    v-model="editRequest.duration"
                    outlined
                    dense
                    min="1"
                    type="number"
                    hide-details="auto"
                    disabled
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>

                <!-- Description -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Description
                  </p>
                  <vue-editor
                    v-model="editRequest.remarks"
                    :editorOptions="editorOptionsNoToolbar"
                    spellcheck="false"
                    style="width: 100%;"
                    disabled
                  />
                  <!-- <v-textarea
                    v-model="editRequest.remarks"
                    :placeholder="`${editRequest.event_type} description`"
                    dense
                    outlined
                    rows="3"
                    hide-details
                    disabled
                  ></v-textarea> -->
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>

        <v-card-actions
          v-if="userRole === 'Secretary'"
          class="mt-2"
        >
          <v-row>
            <v-col cols="3">
              <v-btn
                color="error"
                block
                :disabled="updateLoading"
                @click="isVoidDialogVisible = true"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
                <span>Delete</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="isViewRotationDialogVisible"
      width="500"
      @input="resetEditRequest()"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          {{ editRequest.event_type }}
          <div
            ref="lottieRotation"
            style="margin-left: 7px; height: 50px;"
          ></div>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isViewRotationDialogVisible = false; resetEditRequest()"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1">
              <v-row>
                <!-- Half day period -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                  v-if="editRequest.half_day_period && editRequest.duration === '0.5'"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Half day period:
                  </p>
                  <v-text-field
                    v-model="editRequest.half_day_period"
                    placeholder="Half day period"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>
                </v-col>

                <!-- Event Date -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    {{ editRequest.event_type }} Date Range:
                  </p>
                  <!-- Date range -->
                  <v-menu
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :append-icon="icons.mdiCalendar"
                        :value="editRequestDateRangeText"
                        readonly
                        dense
                        outlined
                        hide-details
                        disabled
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="editRequestDateRange"
                      color="primary"
                      :min="new Date().toISOString().substring(0, 10)"
                      :first-day-of-week="1"
                      range
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        block
                      >
                        {{ t('Set Dates') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Event Type -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Type:
                  </p>
                  <v-select
                    v-model="editRequest.event_type"
                    :items="['Rotation']"
                    dense
                    outlined
                    hide-details="auto"
                    disabled
                    placeholder="Select Event Type"
                  ></v-select>
                </v-col>

                <!-- Event Duration -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    No. of days:
                  </p>
                  <v-text-field
                    v-model="editRequest.duration"
                    outlined
                    dense
                    min="1"
                    type="number"
                    hide-details="auto"
                    disabled
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>

                <!-- Rotation By -->
                <v-col
                  v-if="editRequest.event_type === 'Rotation'"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Rotation By:
                  </p>
                  <v-text-field
                    v-model="editRequest.rotater"
                    placeholder="Rotation by"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>
                </v-col>

                <!-- Original branch -->
                <v-col
                  v-if="editRequest.event_type === 'Rotation'"
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Original branch:
                  </p>
                  <v-text-field
                    v-model="editRequest.original_branch"
                    placeholder="Original branch"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>
                </v-col>

                <!-- New branch -->
                <v-col
                  v-if="editRequest.event_type === 'Rotation'"
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    New branch:
                  </p>
                  <v-text-field
                    v-model="editRequest.new_branch"
                    placeholder="New branch"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>
                </v-col>

                <!-- Description -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Description
                  </p>
                  <vue-editor
                    v-model="editRequest.remarks"
                    :editorOptions="editorOptionsNoToolbar"
                    spellcheck="false"
                    style="width: 100%;"
                    disabled
                  />
                  <!-- <v-textarea
                    v-model="editRequest.remarks"
                    :placeholder="`${editRequest.event_type} description`"
                    dense
                    outlined
                    rows="3"
                    hide-details
                    disabled
                  ></v-textarea> -->
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>

        <v-card-actions
          v-if="userRole === 'Secretary'"
          class="mt-2"
        >
          <v-row>
            <v-col cols="3">
              <v-btn
                color="error"
                block
                :disabled="updateLoading"
                @click="isVoidDialogVisible = true"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
                <span>Delete</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="isViewBlockedDialogVisible"
      width="500"
      @input="resetEditRequest()"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          {{ editRequest.event_type }}
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isViewBlockedDialogVisible = false; resetEditRequest()"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1">
              <v-row>
                <!-- Event Date -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    {{ editRequest.event_type }} Date Range:
                  </p>
                  <!-- Date range -->
                  <v-menu
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :append-icon="icons.mdiCalendar"
                        :value="editRequestDateRangeText"
                        readonly
                        dense
                        outlined
                        hide-details
                        disabled
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="editRequestDateRange"
                      color="primary"
                      :min="new Date().toISOString().substring(0, 10)"
                      :first-day-of-week="1"
                      range
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        block
                      >
                        {{ t('Set Dates') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Event Duration -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    No. of days:
                  </p>
                  <v-text-field
                    v-model="editRequest.duration"
                    outlined
                    dense
                    min="1"
                    type="number"
                    hide-details="auto"
                    disabled
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>

                <!-- Description -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Description
                  </p>
                  <vue-editor
                    v-model="editRequest.remarks"
                    :editorOptions="editorOptionsNoToolbar"
                    spellcheck="false"
                    style="width: 100%;"
                    disabled
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="mt-2">
          <v-row>
            <v-col cols="3">
              <v-btn
                color="error"
                block
                :disabled="updateLoading"
                @click="isVoidDialogVisible = true"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
                <span>Delete</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="isViewEventDialogVisible"
      width="500"
      @input="resetEditEvent()"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          Replacement Invite
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isViewEventDialogVisible = false; resetEditEvent()"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1">
              <v-row>
                <!-- Branch -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Branch:
                  </p>
                  <v-text-field
                    v-model="editEvent.branch"
                    placeholder="Branch"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>
                </v-col>

                <!-- Half day period -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                  v-if="editEvent.half_day_period && editEvent.duration === '0.5'"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Half day period:
                  </p>
                  <v-text-field
                    v-model="editEvent.half_day_period"
                    placeholder="Half day period"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>
                </v-col>

                <!-- Event Date -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Leave Date Range:
                  </p>
                  <!-- Date range -->
                  <v-menu
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :append-icon="icons.mdiCalendar"
                        :value="editDateRangeText"
                        readonly
                        dense
                        outlined
                        hide-details
                        disabled
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="editDateRange"
                      color="primary"
                      :min="new Date().toISOString().substring(0, 10)"
                      :first-day-of-week="1"
                      range
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        block
                      >
                        {{ t('Set Dates') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Leave Type -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Leave Type:
                  </p>
                  <v-select
                    v-model="editEvent.leave_type"
                    :items="leaveTypeOptions"
                    dense
                    outlined
                    hide-details="auto"
                    disabled
                    placeholder="Select Leave Type"
                  ></v-select>
                </v-col>

                <!-- Leave Duration -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    No. of days:
                  </p>
                  <v-text-field
                    v-model="editEvent.duration"
                    outlined
                    dense
                    min="1"
                    type="number"
                    hide-details="auto"
                    disabled
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>

                <!-- Medical Certificate -->
                <v-col
                  v-if="editEvent.leave_type === 'Sick leave' || editEvent.leave_type === 'Maternity leave'"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Medical Certificate:
                  </p>
                  <div v-if="!event.mc_url">
                    <div>
                      <small class="error--text">
                        Requested attachment seems to be missing or corrupted, please ask counter to reattach again
                      </small>
                    </div>
                  </div>

                  <div v-else>
                    <a
                      :href="event.mc_url"
                      target="_blank"
                    >
                      <span>{{ event.mc_name }}</span>
                      <v-icon
                        size="18"
                        color="primary"
                      >{{ icons.mdiOpenInNew }}</v-icon>
                    </a>
                  </div>
                </v-col>
                <v-col
                  v-if="editEvent.leave_type === 'Annual leave' && editEvent.request_replacement_by && editEvent.request_replacement_by.id"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Replacement By:
                  </p>
                  <!-- Request replacement by -->
                  <v-select
                    v-model="editEvent.request_replacement_by.id"
                    :items="accounts"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Request replacement by"
                    disabled
                  ></v-select>
                </v-col>

                <!-- Remarks -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Remarks:
                  </p>
                  <vue-editor
                    v-model="editEvent.remarks"
                    :editorOptions="editorOptionsNoToolbar"
                    spellcheck="false"
                    style="width: 100%;"
                    disabled
                  />
                  <!-- <v-textarea
                    v-model="editEvent.remarks"
                    placeholder="Remarks, if any"
                    dense
                    outlined
                    rows="3"
                    hide-details
                    disabled
                  ></v-textarea> -->
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="mt-2">
          <v-row>
            <v-col cols="6">
              <v-btn
                color="error"
                block
                :disabled="updateLoading"
                @click="updateInviteStatus('Reject')"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiClose }}
                </v-icon>
                <span>Reject</span>
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="primary"
                block
                :disabled="updateLoading"
                @click="updateInviteStatus('Accept')"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiCheck }}
                </v-icon>
                <span>Accept</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="isViewRequestDialogVisible"
      width="500"
      @input="resetEditRequest()"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          Leave Request
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isViewRequestDialogVisible = false; resetEditRequest()"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1">
              <v-row>
                <!-- Branch -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Branch:
                  </p>
                  <v-text-field
                    v-model="editRequest.branch"
                    placeholder="Branch"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>
                </v-col>

                <!-- Half day period -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                  v-if="editRequest.half_day_period && editRequest.duration === '0.5'"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Half day period:
                  </p>
                  <v-text-field
                    v-model="editRequest.half_day_period"
                    placeholder="Half day period"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>
                </v-col>

                <!-- Event Date -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Leave Date Range:
                  </p>
                  <!-- Date range -->
                  <v-menu
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :append-icon="icons.mdiCalendar"
                        :value="editRequestDateRangeText"
                        readonly
                        dense
                        outlined
                        hide-details
                        disabled
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="editRequestDateRange"
                      color="primary"
                      :min="new Date().toISOString().substring(0, 10)"
                      :first-day-of-week="1"
                      range
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        block
                      >
                        {{ t('Set Dates') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Leave Type -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Leave Type:
                  </p>
                  <v-select
                    v-model="editRequest.leave_type"
                    :items="leaveTypeOptions"
                    dense
                    outlined
                    hide-details="auto"
                    disabled
                    placeholder="Select Leave Type"
                  ></v-select>
                </v-col>

                <!-- Leave Duration -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    No. of days:
                  </p>
                  <v-text-field
                    v-model="editRequest.duration"
                    outlined
                    dense
                    min="1"
                    type="number"
                    hide-details="auto"
                    disabled
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>

                <!-- Medical Certificate -->
                <v-col
                  v-if="editRequest.leave_type === 'Sick leave' || editRequest.leave_type === 'Maternity leave'"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Medical Certificate:
                  </p>
                  <div v-if="!editRequest.mc_url">
                    <div>
                      <small class="error--text">
                        Requested attachment seems to be missing or corrupted, please ask counter to reattach again
                      </small>
                    </div>
                  </div>

                  <div v-else>
                    <a
                      :href="editRequest.mc_url"
                      target="_blank"
                    >
                      <span>{{ editRequest.mc_name }}</span>
                      <v-icon
                        size="18"
                        color="primary"
                      >{{ icons.mdiOpenInNew }}</v-icon>
                    </a>
                  </div>
                </v-col>
                <v-col
                  v-if="editRequest.leave_type === 'Annual leave' && editRequest.request_replacement_by && editRequest.request_replacement_by.id"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Replacement By:
                  </p>
                  <!-- Request replacement by -->
                  <v-select
                    v-model="editRequest.request_replacement_by.id"
                    :items="accounts"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Request replacement by"
                    disabled
                  ></v-select>
                </v-col>

                <!-- Remarks -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Remarks:
                  </p>
                  <vue-editor
                    v-model="editRequest.remarks"
                    :editorOptions="editorOptionsNoToolbar"
                    spellcheck="false"
                    style="width: 100%;"
                    disabled
                  />
                  <!-- <v-textarea
                    v-model="editRequest.remarks"
                    placeholder="Remarks, if any"
                    dense
                    outlined
                    rows="3"
                    hide-details
                    disabled
                  ></v-textarea> -->
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="mt-2">
          <!-- Auditor/Secretary actions -->
          <v-row v-if="userRole === 'Auditor' || userRole === 'Secretary'">
            <v-col cols="6">
              <v-btn
                color="error"
                block
                :disabled="updateLoading"
                @click="updateRequestStatus('Reject')"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiClose }}
                </v-icon>
                <span>Reject</span>
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="primary"
                block
                :disabled="updateLoading"
                @click="updateRequestStatus('Approve')"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiCheck }}
                </v-icon>
                <span>Approve</span>
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col cols="3">
              <v-btn
                color="error"
                block
                :disabled="updateLoading"
                @click="isVoidDialogVisible = true"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
                <span>Delete</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="isViewLeaveDialogVisible"
      width="500"
      @input="resetEditRequest()"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          Approved Leave
          <div ref="lottieLeave" style="margin-left: 7px; height: 50px;"></div>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isViewLeaveDialogVisible = false; resetEditRequest()"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1">
              <v-row>
                <!-- Leave By -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Leave By:
                  </p>
                  <v-text-field
                    v-model="editRequest.requester"
                    placeholder="Leave by"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>
                </v-col>

                <!-- Branch -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Branch:
                  </p>
                  <v-text-field
                    v-model="editRequest.branch"
                    placeholder="Branch"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>
                </v-col>

                <!-- Half day period -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                  v-if="editRequest.half_day_period && editRequest.duration === '0.5'"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Half day period:
                  </p>
                  <v-text-field
                    v-model="editRequest.half_day_period"
                    placeholder="Half day period"
                    dense
                    outlined
                    hide-details
                    disabled
                  ></v-text-field>
                </v-col>

                <!-- Event Date -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Leave Date Range:
                  </p>
                  <!-- Date range -->
                  <v-menu
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :append-icon="icons.mdiCalendar"
                        :value="editRequestDateRangeText"
                        readonly
                        dense
                        outlined
                        hide-details
                        disabled
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="editRequestDateRange"
                      color="primary"
                      :min="new Date().toISOString().substring(0, 10)"
                      :first-day-of-week="1"
                      range
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        block
                      >
                        {{ t('Set Dates') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Leave Type -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Leave Type:
                  </p>
                  <v-select
                    v-model="editRequest.leave_type"
                    :items="leaveTypeOptions"
                    dense
                    outlined
                    hide-details="auto"
                    disabled
                    placeholder="Select Leave Type"
                  ></v-select>
                </v-col>

                <!-- Leave Duration -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    No. of days:
                  </p>
                  <v-text-field
                    v-model="editRequest.duration"
                    outlined
                    dense
                    min="1"
                    type="number"
                    hide-details="auto"
                    disabled
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>

                <!-- Medical Certificate -->
                <v-col
                  v-if="editRequest.leave_type === 'Sick leave' || editRequest.leave_type === 'Maternity leave'"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Medical Certificate:
                  </p>
                  <div v-if="!editRequest.mc_url">
                    <div>
                      <small class="error--text">
                        Requested attachment seems to be missing or corrupted, please ask counter to reattach again
                      </small>
                    </div>
                  </div>

                  <div v-else>
                    <a
                      :href="editRequest.mc_url"
                      target="_blank"
                    >
                      <span>{{ editRequest.mc_name }}</span>
                      <v-icon
                        size="18"
                        color="primary"
                      >{{ icons.mdiOpenInNew }}</v-icon>
                    </a>
                  </div>
                </v-col>
                <v-col
                  v-if="editRequest.leave_type === 'Annual leave' && editRequest.request_replacement_by && editRequest.request_replacement_by.id"
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Replacement By:
                  </p>
                  <!-- Request replacement by -->
                  <v-select
                    v-model="editRequest.request_replacement_by.id"
                    :items="accounts"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Request replacement by"
                    disabled
                  ></v-select>
                </v-col>

                <!-- Remarks -->
                <v-col
                  cols="12"
                  :class="{ 'px-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Remarks:
                  </p>
                  <vue-editor
                    v-model="editRequest.remarks"
                    :editorOptions="editorOptionsNoToolbar"
                    spellcheck="false"
                    style="width: 100%;"
                    disabled
                  />
                  <!-- <v-textarea
                    v-model="editRequest.remarks"
                    placeholder="Remarks, if any"
                    dense
                    outlined
                    rows="3"
                    hide-details
                    disabled
                  ></v-textarea> -->
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>

        <v-card-actions
          v-if="(editRequest.requested_by && editRequest.requested_by.id === userData.id) || userRole === 'Secretary'"
          class="mt-2"
        >
          <v-row>
            <v-col cols="3">
              <v-btn
                color="error"
                block
                :disabled="updateLoading || (new Date(editRequest.start_date) < new Date() && userRole !== 'Secretary')"
                @click="isVoidDialogVisible = true"
              >
                <v-icon
                  size="18"
                  class="me-1"
                >
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
                <span>Delete</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Void dialog -->
    <v-dialog
      v-model="isVoidDialogVisible"
      width="500"
    >
      <v-card>
        <v-card-title
          v-if="editRequest"
          class="d-flex align-center mv-4"
        >
          <span v-if="editRequest.event_type === 'Leave'">
            Void leave request on {{ editRequest.start_date_formatted }}
          </span>
          <span v-else-if="editRequest.event_type === 'Leave on behalf'">
            Void leave on {{ editRequest.start_date_formatted }}
          </span>
          <span v-else-if="editRequest.event_type === 'Blocked'">
            Void blocked days on {{ editRequest.start_date_formatted }}
          </span>
          <span v-else>
            Void {{ editRequest.event_type }} on {{ editRequest.start_date_formatted }}
          </span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isVoidDialogVisible = false"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text v-if="editRequest.event_type === 'Leave' || editRequest.event_type === 'Leave on behalf'">
          This request will be voided. It won't be taken into any calculations.
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="error"
            block
            :loading="voidLoading"
            :disabled="voidLoading"
            class="mt-3"
            @click="voidEvent()"
          >
            Yes, remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Branch dialog -->
    <v-dialog
      v-model="isBranchDialogVisible"
      width="90%"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          <span>
            Branches
          </span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isBranchDialogVisible = false"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col
              cols="12"
              md="3"
            >
              <v-card style="min-height: 200px;">
                <v-card-title class="pb-1">
                  <span class="text-h6">Unassigned</span>
                </v-card-title>
                <v-card-text class="text-h6">
                  <v-divider class="divider-width mt-2"></v-divider>
                </v-card-text>
                <v-list>
                  <draggable
                    v-model="unassignedUsers"
                    :sort="false"
                    :group="draggableGroup"
                    item-key="id"
                    @change="handleUnassign"
                    :disabled="updateLoading"
                  >
                    <v-list-item v-for="user in unassignedUsers" :key="user.id">
                      <v-chip>
                        {{ user.name }}
                      </v-chip>
                    </v-list-item>
                  </draggable>
                </v-list>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              md="3"
              v-for="(branch, index) in branches"
              :key="branch.id"
            >
              <v-card style="min-height: 200px;">
                <v-card-title class="pb-1">
                  <span class="text-h6">{{ branch.name }}</span>
                </v-card-title>

                <v-card-text class="text-h6">
                  <v-divider class="divider-width mt-2" :style="{ backgroundColor: branchColors[index] }"></v-divider>
                </v-card-text>

                <v-list>
                  <draggable
                    v-model="branchUsers[branch.id]"
                    :sort="false"
                    :group="draggableGroup"
                    item-key="id"
                    @change="e => handleReassign(e, branch.id)"
                    :disabled="updateLoading"
                  >
                    <v-list-item
                      v-for="user in branchUsers[branch.id]"
                      :key="user.id"
                    >
                      <v-chip :color="branchColors[index]">
                        {{ user.name }}
                      </v-chip>
                    </v-list-item>
                  </draggable>
                </v-list>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="primary"
            block
            :loading="branchLoading"
            :disabled="branchLoading"
            class="mt-3"
            @click="isBranchDialogVisible = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Balance dialog -->
    <v-dialog
      v-model="isBalanceDialogVisible"
      width="90%"
    >
      <v-card>
        <v-card-title class="d-flex align-center mv-4">
          <span>
            Leave Balances
          </span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="isBalanceDialogVisible = false"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col
              cols="12"
              md="3"
              v-for="user in accountBalances"
              :key="user.id"
            >
              <v-card style="min-height: 200px;">
                <v-card-title class="pb-1">
                  <span class="text-h6">{{ user.name }}</span>
                </v-card-title>

                <v-card-text>
                  <v-list
                    v-for="leaveType in leaveTypes"
                    :key="leaveType.key"
                    class="pt-1"
                  >
                    <v-list-item
                      style="min-height: auto;"
                      class="px-0"
                    >
                      <div class="d-flex align-center flex-grow-1 flex-wrap">
                        <div class="me-2">
                          <span class="text-no-wrap">
                            {{ leaveType.label }}
                          </span>
                        </div>
                        <v-spacer></v-spacer>
                        <div class="d-flex align-center">
                          <span class="text--primary me-1">
                            {{ user[leaveType.key] || 0 }}
                          </span>
                        </div>
                      </div>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="primary"
            block
            class="mt-3"
            @click="isBalanceDialogVisible = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notification dialog -->
    <v-dialog
      v-model="isNotificationDialogVisible"
      width="675"
      persistent
    >
      <v-card>
        <v-card-title
          class="d-flex align-center mv-4"
        >
          Your notifications
        </v-card-title>

        <v-card-text>
          <v-row align="stretch">
            <v-col
              v-for="notification in notifications"
              :key="notification.id"
              cols="12"
              sm="6"
            >
              <v-card
                class="mb-4"
                :class="notification.event.event_type === 'Event' ? 'border-info bg-info' :
                        notification.event.event_type === 'Announcement' ? 'border-warning bg-warning' :
                        notification.event.event_type === 'Leave' || notification.event.event_type === 'Leave on behalf' ? 'border-accent bg-accent' : 'border-purple bg-purple'"
                height="100%"
              >
                <div ref="lottieContainers" class="lottie-bg" :data-type="notification.event.event_type"></div>
                <v-card-title class="d-flex flex-column align-start pb-1">
                  <p class="mb-1">
                    {{ notification.event.event_type === 'Leave' || notification.event.event_type === 'Leave on behalf' ? 'Leave Approved' : notification.event.event_type }}
                  </p>
                  <p v-if="notification.event.end_date_formatted !== notification.event.start_date_formatted">
                    {{ notification.event.start_date_formatted }} - {{ notification.event.end_date_formatted }}
                  </p>
                  <p v-else>
                    {{ notification.event.start_date_formatted }} {{ notification.event.duration === '0.5' ? `(${notification.event.half_day_period})` : '' }}
                  </p>
                </v-card-title>
                <v-card-text>
                  {{ notification.event.event_type === 'Leave' || notification.event.event_type === 'Leave on behalf' ? notification.event.leave_type : notification.event.event_type === 'Rotation' ? `To ${notification.event.new_branch}` : cleanRemarks(notification.event.remarks) }}
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="success"
            block
            :loading="notificationLoading"
            :disabled="notificationLoading"
            class="mt-3"
            @click="markAsRead"
          >
            <v-icon
              size="18"
              class="me-1"
            >
              {{ icons.mdiCheck }}
            </v-icon>
            <span>Mark all as read</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiPlus,
  mdiClose,
  mdiCalendar,
  mdiEyeOutline,
  mdiCheck,
  mdiDeleteOutline,
  mdiOpenInNew,
} from '@mdi/js'
import {
  ref,
  onMounted,
  onUnmounted,
  computed,
  watch,
  inject,
  nextTick,
} from '@vue/composition-api'
import Vue from 'vue'
import Alert from '@/components/Alert'
import draggable from 'vuedraggable'
import { VueEditor } from 'vue2-editor'
import LeaveSummaryBreakdown from '@/components/apps/summaries/LeaveSummaryBreakdown.vue'
import lottie from 'lottie-web'
import announcement from '@/assets/lottie/announcement.json'
import approved from '@/assets/lottie/approved.json'
import rotation from '@/assets/lottie/rotation.json'
import eventAnim from '@/assets/lottie/event.json'
import leave from '@/assets/lottie/leave.json'
import receiptStoreModule from '../receipt/receiptStoreModule'

export default {
  components: {
    Alert,
    draggable,
    VueEditor,
    LeaveSummaryBreakdown,
  },
  setup() {
    // Initializers
    const RECEIPT_APP_STORE_MODULE_NAME = 'app-receipt'
    const userData = JSON.parse(localStorage.getItem('userData'))
    const userRole = userData && userData.role ? userData.role : null
    const store = inject('store')
    const snackbarService = inject('snackbarService')
    const t = inject('t')
    const can = inject('can')

    if (!store.hasModule(RECEIPT_APP_STORE_MODULE_NAME)) {
      store.registerModule(RECEIPT_APP_STORE_MODULE_NAME, receiptStoreModule)
    }

    // Properties
    const userBranch = ref(null)
    const type = 'month'
    const mode = 'stack'
    const weekday = [1, 2, 3, 4, 5, 6, 0]
    const focus = ref('')
    const events = ref([])
    const calendar = ref(null)
    const isLeaveDialogVisible = ref(false)
    const isEventDialogVisible = ref(false)
    const isBlockDialogVisible = ref(false)
    const isViewEventDialogVisible = ref(false)
    const isViewRequestDialogVisible = ref(false)
    const isViewLeaveDialogVisible = ref(false)
    const isViewCompanyEventDialogVisible = ref(false)
    const isViewRotationDialogVisible = ref(false)
    const isViewBlockedDialogVisible = ref(false)
    const isVoidDialogVisible = ref(false)
    const isBranchDialogVisible = ref(false)
    const isBalanceDialogVisible = ref(false)
    const isNotificationDialogVisible = ref(false)
    const isConfirmDialogVisible = ref(false)
    const leaveTypeOptions = ref([])
    const event = ref({
      start_date: new Date().toISOString().substring(0, 10),
      end_date: new Date().toISOString().substring(0, 10),
      event_type: null,
      leave_type: null,
      duration: 1,
      half_day_period: null,
      mc: null,
      requested_by_id: userData.id,
      rotation_by_id: null,
      original_branch_id: null,
      new_branch_id: null,
      remarks: null,
      request_replacement_by_id: null,
    })
    const editEvent = ref({
      start_date: null,
      end_date: null,
      event_type: null,
      leave_type: null,
      duration: null,
      half_day_period: null,
      mc: null,
      requested_by_id: null,
      rotater: null,
      original_branch: null,
      new_branch: null,
      remarks: null,
      branch: null,
    })
    const editRequest = ref({
      start_date: null,
      end_date: null,
      event_type: null,
      leave_type: null,
      duration: null,
      half_day_period: null,
      mc: null,
      requested_by_id: null,
      rotater: null,
      original_branch: null,
      new_branch: null,
      remarks: null,
      branch: null,
    })
    const updateLoading = ref(false)
    const uploadLoading = ref(false)
    const voidLoading = ref(false)
    const branchLoading = ref(false)
    const notificationLoading = ref(false)
    const mcUploadRules = [value => !value || value.size < 5000000 || 'MC file has to be lower than 5MB']
    const saveLoading = ref(false)
    const errors = ref([])
    const isDateMenuOpen = ref(false)
    const isSummaryDateMenuOpen = ref(false)
    const [dayNow, monthNow, yearNow] = new Date().toLocaleDateString('en-SG').split('/')
    const dateRange = ref([`${yearNow}-${monthNow}-${dayNow}`])
    const summaryDateRange = ref([`${yearNow}-${monthNow}-${dayNow}`])
    const editDateRange = ref([`${yearNow}-${monthNow}-${dayNow}`])
    const editRequestDateRange = ref([`${yearNow}-${monthNow}-${dayNow}`])
    const fetchLoading = ref(false)
    const accounts = ref([])
    const accountBalances = ref([])
    const leaveBalance = ref({})
    const isHalfDay = ref(false)
    const leaveSummary = ref({})
    const invites = ref([])
    const notifications = ref([])
    const leaveTypes = [
      { label: 'Annual Leave', key: 'annual_leave' },
      { label: 'Emergency Leave', key: 'emergency_leave' },
      { label: 'Sick Leave', key: 'sick_leave' },
      { label: 'Maternity Leave', key: 'maternity_leave' },
      { label: 'Compassionate Leave', key: 'compassionate_leave' },
      { label: 'Paternity Leave', key: 'paternity_leave' },
      { label: 'Total Rotations', key: 'rotations' },
    ]
    const announcementAnimation = announcement
    const eventAnimation = eventAnim
    const approvedAnimation = approved
    const rotationAnimation = rotation
    const leaveAnimation = leave
    const lottieContainers = ref([])
    const lottieInstances = []
    const lottieLeave = ref(null)
    let lottieLeaveInstance = null
    const lottieEvent = ref(null)
    let lottieEventInstance = null
    const lottieRotation = ref(null)
    let lottieRotationInstance = null
    const inviteTablePagination = ref({
      current_page: 1,
      per: 20,
      total_count: 0,
      total_page: 0,
    })
    const inviteListLengthOptions = ref([20, 50, 100])
    const requests = ref([])
    const requestTablePagination = ref({
      current_page: 1,
      per: 20,
      total_count: 0,
      total_page: 0,
    })
    const requestListLengthOptions = ref([20, 50, 100])
    const blockedDates = ref([])
    const blockedDatesList = ref([])
    const blockTablePagination = ref({
      current_page: 1,
      per: 20,
      total_count: 0,
      total_page: 0,
    })
    const blockListLengthOptions = ref([20, 50, 100])
    const logTablePagination = ref({
      current_page: 1,
      per: 20,
      total_count: 0,
      total_page: 0,
    })
    const logListLengthOptions = ref([20, 50, 100])
    const logs = ref([])
    const unassignedUsers = ref([])
    const removeReplacementUsers = ref([15, 17, 21, 23])
    const soloClerkReplacement = ref([6, 14, 31])
    const branchUsers = ref({})
    const branchColors = [
      'rgba(255, 99, 132)',
      'rgba(75, 192, 192)',
      'rgba(255, 159, 64)',
      'rgba(54, 162, 235)',
      'rgba(255, 205, 86)',
      'rgba(153, 102, 255)',
      'rgba(255, 102, 255)',
    ]
    const editorOptions = {
      modules: {
        toolbar: true,
      },
    }
    const editorOptionsNoToolbar = {
      modules: {
        toolbar: false,
      },
    }

    // Utils
    const formatDate = date => {
      if (!date) return null
      const [year, month, day] = date.split('-')

      return `${day}/${month}/${year}`
    }

    // Computed
    const branches = computed(() => store.state.branches.filter(b => b.active && b.id !== 9))
    const dateRangeText = computed(() => {
      // Handle if dateRange is a string (single date)
      if (typeof dateRange.value === 'string') {
        return formatDate(dateRange.value)
      }

      // If dateRange is not an array or has less than 2 items, return empty or single formatted date
      if (!Array.isArray(dateRange.value) || dateRange.value.length < 2) {
        return dateRange.value && dateRange.value[0] ? formatDate(dateRange.value[0]) : ''
      }

      if (Date.parse(dateRange.value[0]) > Date.parse(dateRange.value[1])) {
        const [start, end] = dateRange.value
        dateRange.value[0] = end
        dateRange.value[1] = start
      }

      const startDateText = formatDate(dateRange.value[0])
      const showEndDate = dateRange.value[0] !== dateRange.value[1] && formatDate(dateRange.value[1]) !== null
      const endDateText = showEndDate ? ` - ${formatDate(dateRange.value[1])}` : ''

      return `${startDateText}${endDateText}`
    })
    const summaryDateRangeText = computed(() => {
      // Handle if dateRange is a string (single date)
      if (typeof summaryDateRange.value === 'string') {
        return formatDate(summaryDateRange.value)
      }

      if (!Array.isArray(summaryDateRange.value) || summaryDateRange.value.length < 2) {
        return summaryDateRange.value && summaryDateRange.value[0] ? formatDate(summaryDateRange.value[0]) : ''
      }

      if (Date.parse(summaryDateRange.value[0]) > Date.parse(summaryDateRange.value[1])) {
        const [start, end] = summaryDateRange.value
        summaryDateRange.value[0] = end
        summaryDateRange.value[1] = start
      }

      const startDateText = formatDate(summaryDateRange.value[0])
      const showEndDate = summaryDateRange.value[0] !== summaryDateRange.value[1] && formatDate(summaryDateRange.value[1]) !== null
      const endDateText = showEndDate ? ` - ${formatDate(summaryDateRange.value[1])}` : ''

      return `${startDateText}${endDateText}`
    })
    const editDateRangeText = computed(() => {
      if (!editEvent.value.start_date_formatted) return ''

      const startDateText = formatDate(new Date(editEvent.value.start_date_formatted.replace(/(..)\/(..)\/(....)/, '$3-$2-$1')).toISOString().substring(0, 10))
      const showEndDate = editEvent.value.start_date_formatted !== editEvent.value.end_date_formatted
      const endDateText = showEndDate ? ` - ${formatDate(new Date(editEvent.value.end_date_formatted.replace(/(..)\/(..)\/(....)/, '$3-$2-$1')).toISOString().substring(0, 10))}` : ''

      return `${startDateText}${endDateText}`
    })
    const editRequestDateRangeText = computed(() => {
      if (!editRequest.value.start_date_formatted) return ''

      const startDateText = formatDate(new Date(editRequest.value.start_date_formatted.replace(/(..)\/(..)\/(....)/, '$3-$2-$1')).toISOString().substring(0, 10))
      const showEndDate = editRequest.value.start_date_formatted !== editRequest.value.end_date_formatted
      const endDateText = showEndDate ? ` - ${formatDate(new Date(editRequest.value.end_date_formatted.replace(/(..)\/(..)\/(....)/, '$3-$2-$1')).toISOString().substring(0, 10))}` : ''

      return `${startDateText}${endDateText}`
    })
    const draggableGroup = computed(() => {
      if (userRole === 'Secretary') return 'users'

      return { name: 'users', pull: false, put: false }
    })
    const inviteTableColumns = computed(() => (
      [
        { text: t('Actions'), value: 'actions' },
        { text: t('Date Range'), value: 'date_range' },
        { text: t('Requester'), value: 'requester' },
        { text: t('No. of days'), value: 'duration', align: 'center' },
        { text: t('Leave Type'), value: 'leave_type' },
      ]
    ))
    const requestTableColumns = computed(() => (
      [
        { text: t('Actions'), value: 'actions' },
        { text: t('Date Range'), value: 'date_range' },
        { text: t('Requester'), value: 'requester' },
        { text: t('No. of days'), value: 'duration', align: 'center' },
        { text: t('Leave Type'), value: 'leave_type' },
      ]
    ))
    const blockTableColumns = computed(() => (
      [
        { text: t('Actions'), value: 'actions' },
        { text: t('Date Range'), value: 'date_range' },
        { text: t('Description'), value: 'remarks' },
        { text: t('No. of days'), value: 'duration', align: 'center' },
      ]
    ))
    const logTableColumns = computed(() => (
      [
        { text: t('Datetime'), value: 'created_at' },
        { text: t('Description'), value: 'description' },
      ]
    ))

    // Methods
    const fetchClerks = () => {
      store
        .dispatch('app-receipt/fetchClerks', { role: [1, 4] })
        .then(response => {
          const { data } = response.data
          accounts.value = data.filter(acc => acc.is_active)
          const findUser = data.find(acc => acc.id === userData.id)
          userBranch.value = findUser && findUser.branch ? findUser.branch : null
        })
        .catch(error => snackbarService.error(error.response.data.message || 'Something went wrong while fetching accounts. Please refresh!'))
    }
    const fetchBalances = () => {
      store
        .dispatch('eventStore/fetchBalances', { role: [1, 4, 9] })
        .then(response => {
          const { data } = response.data
          accountBalances.value = data.filter(acc => acc.is_active)
          userBranch.value = data.find(acc => acc.id === userData.id).branch
        })
        .catch(error => snackbarService.error(error.response.data.message || 'Something went wrong while fetching balances. Please refresh!'))
    }
    const dateRangeChange = () => {
      isDateMenuOpen.value = false
      if (isHalfDay.value) {
        const singleDate = Array.isArray(dateRange.value)
          ? dateRange.value[0]
          : dateRange.value

        event.value.start_date = singleDate
        event.value.end_date = singleDate
        event.value.duration = 0.5
      } else if (dateRange.value.length === 2) {
        [event.value.start_date, event.value.end_date] = dateRange.value
        const timeDifference = new Date(dateRange.value[1]).getTime() - new Date(dateRange.value[0]).getTime()
        event.value.duration = Math.floor(timeDifference / (1000 * 3600 * 24)) + 1
      } else {
        [event.value.start_date, event.value.end_date] = [dateRange.value[0], dateRange.value[0]]
        event.value.duration = 1
      }
    }
    const setToday = () => {
      focus.value = ''
    }
    const rnd = (a, b) => Math.floor((b - a + 1) * Math.random()) + a
    const getEventColor = e => {
      switch (e.event_type) {
        case 'Leave':
        case 'Leave on behalf':
          return 'accent'
        case 'Event':
          return 'info'
        case 'Announcement':
          return 'warning'
        case 'Public holiday':
          return 'success'
        case 'Rotation':
          return 'purple'
        default:
          return 'secondary'
      }
    }
    const fetchEventLogs = () => {
      fetchLoading.value = true
      store
        .dispatch('eventStore/fetchEventLogs', {
          page: logTablePagination.value.current_page,
          per: logTablePagination.value.per,
        })
        .then(response => {
          const { data, pagination } = response.data
          logs.value = data
          logTablePagination.value = pagination
          fetchLoading.value = false
        })
        .catch(error => {
          fetchLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching logs. Please refresh!')
        })
    }
    const allowedDates = val => !blockedDates.value.includes(val)
    const populateBranchUsers = b => {
      unassignedUsers.value = accounts.value.filter(acc => !acc.branch)

      b.forEach(branch => {
        Vue.set(
          branchUsers.value,
          branch.id,
          accounts.value.filter(acc => acc.branch && acc.branch.id === branch.id),
        )
      })
    }
    const updateUserBranch = (userId, branchId) => {
      updateLoading.value = true
      store
        .dispatch('eventStore/updateUserBranch', { id: userId, branch_id: branchId })
        .then(response => {
          snackbarService.success(response.data.message)
          unassignedUsers.value.forEach(user => {
            user.branch = null
          })

          Object.entries(branchUsers.value).forEach(([bId, users]) => {
            users.forEach(user => {
              user.branch = { id: bId }
            })
          })
          fetchClerks()
          fetchEventLogs()
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while updating user branch. Please refresh!')
        })
        .finally(updateLoading.value = false)
    }
    const handleUnassign = e => {
      if (e.added && e.added.element) {
        const user = e.added.element
        updateUserBranch(user.id, null)
      }
    }
    const handleReassign = (e, branchId) => {
      if (e.added && e.added.element) {
        const user = e.added.element
        updateUserBranch(user.id, branchId)
      }
    }
    const fetchLeaveTypes = () => {
      store
        .dispatch('eventStore/fetchLeaveTypes')
        .then(response => {
          const { data } = response.data
          leaveTypeOptions.value = data
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching leave types. Please refresh!')
        })
    }
    const fetchLeaveBalance = () => {
      store
        .dispatch('eventStore/fetchLeaveBalance')
        .then(response => {
          const { data } = response.data
          leaveBalance.value = data
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching leave balance. Please refresh!')
        })
    }
    const fetchPendingLeaveInvites = () => {
      store
        .dispatch('fetchPendingLeaveInvites')
        .then(response => {
          const { data } = response.data
          store.dispatch('setPendingLeaveInvites', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching pending leave invites. Please refresh!')
        })
    }
    const fetchPendingLeaveRequests = () => {
      store
        .dispatch('fetchPendingLeaveRequests')
        .then(response => {
          const { data } = response.data
          store.dispatch('setPendingLeaveRequests', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching pending leave requests. Please refresh!')
        })
    }
    const fetchInvites = () => {
      fetchLoading.value = true
      store
        .dispatch('eventStore/fetchInvites', {
          page: inviteTablePagination.value.current_page,
          per: inviteTablePagination.value.per,
        })
        .then(response => {
          const { data, pagination } = response.data
          invites.value = data
          inviteTablePagination.value = pagination
          fetchLoading.value = false
        })
        .catch(error => {
          fetchLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching invites. Please refresh!')
        })
    }
    const invitePageChange = () => {
      fetchInvites()
    }
    const inviteListLengthChange = () => {
      inviteTablePagination.value.current_page = 1
      fetchInvites()
    }
    const fetchRequests = () => {
      fetchLoading.value = true
      store
        .dispatch('eventStore/fetchRequests', {
          page: requestTablePagination.value.current_page,
          per: requestTablePagination.value.per,
        })
        .then(response => {
          const { data, pagination } = response.data
          requests.value = data
          requestTablePagination.value = pagination
          fetchLoading.value = false
        })
        .catch(error => {
          fetchLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching requests. Please refresh!')
        })
    }
    const requestPageChange = () => {
      fetchRequests()
    }
    const requestListLengthChange = () => {
      requestTablePagination.value.current_page = 1
      fetchRequests()
    }
    const initAnimations = () => {
      lottieInstances.forEach(anim => anim.destroy())
      lottieInstances.length = 0

      lottieContainers.value.forEach(container => {
        let animationData

        if (container.dataset.type === 'Announcement') {
          animationData = announcementAnimation
        } else if (container.dataset.type === 'Event') {
          animationData = eventAnimation
        } else if (container.dataset.type === 'Leave' || container.dataset.type === 'Leave on behalf') {
          animationData = approvedAnimation
        } else if (container.dataset.type === 'Rotation') {
          animationData = rotationAnimation
        }

        const anim = lottie.loadAnimation({
          container,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData,
        })

        lottieInstances.push(anim)
      })
    }
    const fetchNotifications = () => {
      fetchLoading.value = true
      store
        .dispatch('eventStore/fetchNotifications')
        .then(response => {
          notifications.value = response.data.data
          fetchLoading.value = false
          if (notifications.value.length > 0) {
            isNotificationDialogVisible.value = true
            nextTick(() => {
              initAnimations()
            })
          }
        })
        .catch(error => {
          fetchLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching notifications. Please refresh!')
        })
    }
    const markAsRead = () => {
      notificationLoading.value = true
      store
        .dispatch('eventStore/markAsRead')
        .then(response => {
          notificationLoading.value = false
          snackbarService.success(response.data.message)
          isNotificationDialogVisible.value = false
          fetchNotifications()
          fetchPendingLeaveInvites()
          fetchPendingLeaveRequests()
        })
        .catch(error => {
          notificationLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while marking notifications. Please refresh!')
        })
    }
    const formatEventName = e => {
      const isUnpaidLeave = e.unpaid_leave_days > 0 || e.leave_type === 'Unpaid leave'
      const isHalfDayPeriod = e.duration === '0.5' && e.half_day_period
      const isLeave = e.event_type === 'Leave' || e.event_type === 'Leave on behalf'
      const isRotation = e.event_type === 'Rotation'
      let baseName
      if (isUnpaidLeave) baseName = 'Unpaid leave'
      else if (isLeave) baseName = 'Leave'
      else baseName = e.event_type

      if (isLeave) {
        const replacementText = e.replacement ? `, replacement by ${e.replacement}` : ''

        if (isHalfDayPeriod) return `${isUnpaidLeave ? 'Unpaid ' : ''}Half day leave (${e.half_day_period}) - ${e.requester}${replacementText}`

        return `${baseName} - ${e.requester}${replacementText}`
      }
      if (isRotation) {
        if (isHalfDayPeriod) {
          return `Half day rotation (${e.half_day_period}) - ${e.rotater} from ${e.original_branch} to ${e.new_branch}`
        }

        return `${baseName} - ${e.rotater} from ${e.original_branch} to ${e.new_branch}`
      }
      const plainRemarks = e.remarks?.replace(/<[^>]*>?/gm, ' ') || ''

      return `${baseName} - ${plainRemarks}`
    }
    const fetchEvents = () => {
      fetchLoading.value = true
      let calendarMonth
      let calendarYear
      if (!focus.value) {
        [, calendarMonth, calendarYear] = new Date().toLocaleDateString('en-SG').split('/')
      } else {
        [, calendarMonth, calendarYear] = new Date(focus.value).toLocaleDateString('en-SG').split('/')
      }
      let events1 = []
      store
        .dispatch('eventStore/fetchEvents', {
          year: calendarYear,
          month: calendarMonth,
        })
        .then(response => {
          events1 = response.data.data
          fetchLoading.value = false
          events.value = events1.map(e => ({
            name: formatEventName(e),
            start: (e.start_date).substring(0, 10),
            end: (e.end_date).substring(0, 10),
            color: getEventColor(e),
            event: e,
          }))
        })
        .catch(error => {
          fetchLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching events. Please refresh!')
        })
    }
    const fetchLeaveSummary = () => {
      fetchLoading.value = true
      store
        .dispatch('eventStore/fetchLeaveSummary', {
          start_date: summaryDateRange.value[0],
          end_date: summaryDateRange.value[1] ? summaryDateRange.value[1] : summaryDateRange.value[0],
        })
        .then(response => {
          leaveSummary.value = response.data.data
          fetchLoading.value = false
        })
        .catch(error => {
          fetchLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching summary. Please refresh!')
        })
    }
    const summaryDateRangeChange = () => {
      isSummaryDateMenuOpen.value = false
      fetchLeaveSummary()
    }
    const fetchBlockedDates = () => {
      fetchLoading.value = true
      store
        .dispatch('eventStore/fetchBlockedDates')
        .then(response => {
          blockedDates.value = response.data.data
          fetchLoading.value = false
        })
        .catch(error => {
          fetchLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching blocked days. Please refresh!')
        })
    }
    const fetchBlockedDatesList = () => {
      fetchLoading.value = true
      store
        .dispatch('eventStore/fetchBlockedDates', {
          list: true,
          page: blockTablePagination.value.current_page,
          per: blockTablePagination.value.per,
        })
        .then(response => {
          const { data, pagination } = response.data
          blockedDatesList.value = data
          blockTablePagination.value = pagination
          fetchLoading.value = false
        })
        .catch(error => {
          fetchLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching blocked days. Please refresh!')
        })
    }
    const blockPageChange = () => {
      fetchBlockedDatesList()
    }
    const blockListLengthChange = () => {
      blockTablePagination.value.current_page = 1
      fetchBlockedDatesList()
    }
    const logPageChange = () => {
      fetchEventLogs()
    }
    const logListLengthChange = () => {
      logTablePagination.value.current_page = 1
      fetchEventLogs()
    }
    const updateInviteStatus = action => {
      updateLoading.value = true
      store
        .dispatch('eventStore/updateInviteStatus', { id: editEvent.value.id, invite_action: action })
        .then(response => {
          snackbarService.success(response.data.message)
          updateLoading.value = false
          isViewEventDialogVisible.value = false
          fetchEvents()
          fetchInvites()
          fetchPendingLeaveInvites()
          fetchRequests()
          fetchBlockedDates()
          fetchEventLogs()
        })
        .catch(error => {
          updateLoading.value = false
          snackbarService.error(`${error.response.data.message}///${error.response.data.errors[0]}` || 'Something went wrong while updating invite. Please refresh!')
        })
    }
    const updateRequestStatus = action => {
      updateLoading.value = true
      store
        .dispatch('eventStore/updateRequestStatus', { id: editRequest.value.id, request_action: action })
        .then(response => {
          snackbarService.success(response.data.message)
          updateLoading.value = false
          isViewRequestDialogVisible.value = false
          fetchEvents()
          fetchInvites()
          fetchRequests()
          fetchPendingLeaveRequests()
          fetchBlockedDates()
          fetchEventLogs()
        })
        .catch(error => {
          updateLoading.value = false
          snackbarService.error(`${error.response.data.message}///${error.response.data.errors[0]}` || 'Something went wrong while updating request. Please refresh!')
        })
    }
    const directUploadMC = file => {
      if (file == null) {
        event.value.mc = null
        event.value.mc_name = null

        return
      }

      uploadLoading.value = true
      store
        .dispatch('eventStore/directUploadMC', file)
        .then(response => {
          uploadLoading.value = false
          if (response) {
            event.value.mc = response.signed_id
            event.value.mc_name = response.filename
          }
        })
        .catch(error => {
          uploadLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while uploading MC. Please refresh!')
        })
    }
    const resetEvent = () => {
      dateRange.value = [`${yearNow}-${monthNow}-${dayNow}`]
      isHalfDay.value = false
      event.value = {
        start_date: new Date().toISOString().substring(0, 10),
        end_date: new Date().toISOString().substring(0, 10),
        event_type: null,
        leave_type: null,
        duration: 1,
        half_day_period: null,
        mc: null,
        requested_by_id: userData.id,
        rotation_by_id: null,
        original_branch_id: null,
        new_branch_id: null,
        remarks: null,
      }
      errors.value = []
    }
    const resetEditEvent = () => {
      editDateRange.value = [`${yearNow}-${monthNow}-${dayNow}`]
      editEvent.value = {
        start_date: null,
        end_date: null,
        event_type: null,
        leave_type: null,
        duration: null,
        half_day_period: null,
        mc: null,
        requested_by_id: null,
        rotater: null,
        original_branch: null,
        new_branch: null,
        remarks: null,
      }
    }
    const saveEvent = () => {
      delete event.value.mc_url
      delete event.value.mc_name
      saveLoading.value = true
      errors.value = []
      store
        .dispatch('eventStore/createEvent', event.value)
        .then(response => {
          saveLoading.value = false
          snackbarService.success(response.data.message)
          fetchEvents()
          fetchLeaveBalance()
          fetchInvites()
          fetchRequests()
          fetchBlockedDates()
          fetchBlockedDatesList()
          fetchEventLogs()
          isLeaveDialogVisible.value = false
          isEventDialogVisible.value = false
          isBlockDialogVisible.value = false
          if (isConfirmDialogVisible.value) isConfirmDialogVisible.value = false
          resetEvent()
        })
        .catch(error => {
          saveLoading.value = false
          errors.value = error.response.data.errors
          snackbarService.error(error.response.data.message || 'Something went wrong while saving event. Please refresh!')
        })
    }
    const resetEditRequest = () => {
      editRequestDateRange.value = [`${yearNow}-${monthNow}-${dayNow}`]
      editRequest.value = {
        start_date: null,
        end_date: null,
        event_type: null,
        leave_type: null,
        duration: null,
        half_day_period: null,
        mc: null,
        requested_by_id: null,
        rotater: null,
        original_branch: null,
        new_branch: null,
        remarks: null,
      }
    }
    const voidEvent = () => {
      voidLoading.value = true
      store
        .dispatch('eventStore/voidEvent', { id: editRequest.value.id })
        .then(response => {
          snackbarService.success(response.data.message)
          fetchRequests()
          fetchLeaveBalance()
          fetchEvents()
          fetchBlockedDates()
          fetchBlockedDatesList()
          fetchEventLogs()
          isViewRequestDialogVisible.value = false
          isViewLeaveDialogVisible.value = false
          isViewCompanyEventDialogVisible.value = false
          isViewBlockedDialogVisible.value = false
          isViewRotationDialogVisible.value = false
          isVoidDialogVisible.value = false
          voidLoading.value = false
        })
        .catch(error => {
          voidLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while voiding event. Please refresh!')
        })
    }
    const isBlocked = date => blockedDates.value.includes(date)
    const eventClicked = e => {
      if (e.event_type === 'Leave' || e.event_type === 'Leave on behalf') {
        isViewLeaveDialogVisible.value = true
        editRequest.value = e
      } else if (e.event_type === 'Event' || e.event_type === 'Announcement') {
        isViewCompanyEventDialogVisible.value = true
        editRequest.value = e
      } else if (e.event_type === 'Rotation') {
        isViewRotationDialogVisible.value = true
        editRequest.value = e
      }
    }
    const fillOriginalBranch = () => {
      const account = accounts.value.find(acc => acc.id === event.value.rotation_by_id)
      if (account.branch) {
        event.value.original_branch_id = account.branch.id
      } else {
        event.value.original_branch_id = null
      }
    }
    const cleanRemarks = remarks => {
      if (remarks) return remarks.replace(/<[^>]*>?/gm, ' ')

      return ''
    }
    const eventTypeChange = () => {
      event.value.leave_type = null
      event.value.rotation_by_id = null
      event.value.original_branch_id = null
      event.value.new_branch_id = null
      event.value.half_day_period = null
      isHalfDay.value = false
      if (event.value.event_type !== 'Leave on behalf') {
        event.value.requested_by_id = userData.id
      } else {
        event.value.requested_by_id = null
      }
      userBranch.value = null
    }
    const getUserBranch = () => {
      userBranch.value = accounts.value.find(acc => acc.id === event.value.requested_by_id).branch
    }
    const confirmLessDur = () => {
      if (!event.value) return true

      return event.value.duration < 1.1
    }

    onMounted(() => {
      fetchEvents()
      fetchLeaveSummary()
      fetchClerks()
      fetchInvites()
      fetchPendingLeaveInvites()
      fetchPendingLeaveRequests()
      fetchRequests()
      fetchBlockedDates()
      fetchBlockedDatesList()
      fetchLeaveTypes()
      fetchLeaveBalance()
      fetchEventLogs()
      fetchNotifications()
    })

    // Watch
    watch(isHalfDay, newVal => {
      event.value.half_day_period = null
      if (newVal === true) {
        event.value.duration = 0.5
        if (Array.isArray(dateRange.value)) {
          const [singleDate] = dateRange.value
          dateRange.value = singleDate
        }
      } else if (!Array.isArray(dateRange.value)) {
        dateRange.value = [dateRange.value, dateRange.value]
      }

      dateRangeChange()
    })

    watch(isViewLeaveDialogVisible, val => {
      if (val) {
        nextTick(() => {
          if (!lottieLeave.value) return
          lottieLeaveInstance = lottie.loadAnimation({
            container: lottieLeave.value,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: leaveAnimation,
          })
        })
      }
      if (lottieLeaveInstance) {
        lottieLeaveInstance.destroy()
        lottieLeaveInstance = null
      }
    })

    watch(isViewCompanyEventDialogVisible, val => {
      if (val) {
        nextTick(() => {
          if (!lottieEvent.value) return

          let animationData

          if (lottieEvent.value.dataset.type === 'Announcement') {
            animationData = announcementAnimation
          } else if (lottieEvent.value.dataset.type === 'Event') {
            animationData = eventAnimation
          }

          lottieEventInstance = lottie.loadAnimation({
            container: lottieEvent.value,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData,
          })
        })
      }
      if (lottieEventInstance) {
        lottieEventInstance.destroy()
        lottieEventInstance = null
      }
    })

    watch(isViewRotationDialogVisible, val => {
      if (val) {
        nextTick(() => {
          if (!lottieRotation.value) return
          lottieRotationInstance = lottie.loadAnimation({
            container: lottieRotation.value,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: rotationAnimation,
          })
        })
      }
      if (lottieRotationInstance) {
        lottieRotationInstance.destroy()
        lottieRotationInstance = null
      }
    })

    // Unmounted
    onUnmounted(() => {
      if (store.hasModule(RECEIPT_APP_STORE_MODULE_NAME)) store.unregisterModule(RECEIPT_APP_STORE_MODULE_NAME)
    })

    return {
      t,
      can,
      userData,
      userRole,
      branches,
      unassignedUsers,
      branchUsers,
      populateBranchUsers,
      handleUnassign,
      handleReassign,
      branchColors,
      type,
      mode,
      weekday,
      focus,
      events,
      formatDate,
      setToday,
      getEventColor,
      rnd,
      calendar,
      isLeaveDialogVisible,
      isEventDialogVisible,
      isBlockDialogVisible,
      isViewEventDialogVisible,
      isViewRequestDialogVisible,
      isViewLeaveDialogVisible,
      isViewCompanyEventDialogVisible,
      isViewRotationDialogVisible,
      leaveTypeOptions,
      fetchLeaveTypes,
      event,
      editEvent,
      uploadLoading,
      mcUploadRules,
      directUploadMC,
      saveEvent,
      saveLoading,
      errors,
      isDateMenuOpen,
      dateRangeText,
      dateRange,
      dateRangeChange,
      editDateRangeText,
      editDateRange,
      editRequestDateRangeText,
      editRequestDateRange,
      fetchEvents,
      fetchLoading,
      accounts,
      accountBalances,
      fetchClerks,
      fetchBalances,
      fetchInvites,
      invites,
      fetchLeaveBalance,
      leaveBalance,
      resetEvent,
      resetEditEvent,
      inviteTableColumns,
      inviteTablePagination,
      inviteListLengthOptions,
      invitePageChange,
      inviteListLengthChange,
      updateLoading,
      updateInviteStatus,
      requests,
      requestTableColumns,
      requestTablePagination,
      requestListLengthOptions,
      requestPageChange,
      requestListLengthChange,
      fetchRequests,
      resetEditRequest,
      updateRequestStatus,
      editRequest,
      isVoidDialogVisible,
      isBranchDialogVisible,
      isBalanceDialogVisible,
      voidEvent,
      voidLoading,
      branchLoading,
      allowedDates,
      fetchBlockedDates,
      fetchBlockedDatesList,
      blockPageChange,
      blockListLengthChange,
      blockTableColumns,
      blockTablePagination,
      blockListLengthOptions,
      logTableColumns,
      logTablePagination,
      logListLengthOptions,
      logPageChange,
      logListLengthChange,
      logs,
      isViewBlockedDialogVisible,
      blockedDatesList,
      userBranch,
      isBlocked,
      editorOptions,
      editorOptionsNoToolbar,
      eventClicked,
      draggableGroup,
      isHalfDay,
      fillOriginalBranch,
      cleanRemarks,
      eventTypeChange,
      getUserBranch,
      leaveSummary,
      summaryDateRange,
      summaryDateRangeText,
      summaryDateRangeChange,
      isSummaryDateMenuOpen,
      notifications,
      isNotificationDialogVisible,
      notificationLoading,
      markAsRead,
      lottieContainers,
      lottieLeave,
      lottieEvent,
      lottieRotation,
      leaveTypes,
      removeReplacementUsers,
      soloClerkReplacement,
      isConfirmDialogVisible,
      confirmLessDur,

      icons: {
        mdiChevronLeft,
        mdiChevronRight,
        mdiPlus,
        mdiClose,
        mdiCalendar,
        mdiEyeOutline,
        mdiCheck,
        mdiDeleteOutline,
        mdiOpenInNew,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
#calendar.theme--light.v-calendar-weekly {
  ::v-deep .v-calendar-weekly__day.v-outside {
    background-color: #f7f7f7 !important;
  }
}
#calendar.theme--light.v-calendar-weekly {
  ::v-deep .v-calendar-weekly__day:has(.blocked-day) {
    background-color: rgba(255, 0, 0, 0.2) !important;
  }
}
#calendar.theme--dark.v-calendar-weekly {
  ::v-deep .v-calendar-weekly__day.v-outside {
    background-color: #5a5773 !important;
  }
}
#calendar.theme--dark.v-calendar-weekly {
  ::v-deep .v-calendar-weekly__day:has(.blocked-day) {
    background-color: rgba(255, 0, 0, 0.3) !important;
  }
}
#calendar.v-calendar-weekly {
  ::v-deep .v-calendar-weekly__week {
    min-height: 100px;
  }
}
.divider-width {
  border-width: 2px;
}
.dark-divider {
  border-right: 1px solid rgba(231, 227, 252, 0.14) !important;
}

.light-divider {
  border-right: 1px solid rgba(94, 86, 105, 0.14);
}
.lottie-bg {
  position: absolute;
  bottom: 10px;
  width: 100%;
  height: 100px;
  z-index: 0;
  opacity: 0.2;
  overflow: hidden;
}
.border-info {
  border: 2px solid var(--v-info-base);
}
.border-warning {
  border: 2px solid var(--v-warning-base);
}
.border-accent {
  border: 2px solid var(--v-accent-base);
}
.border-purple {
  border: 2px solid var(--v-purple-base);
}
.bg-info {
  background-color: rgba(145, 85, 253, 0.05) !important;
}
.bg-warning {
  background-color: rgba(255, 180, 0, 0.05) !important;
}
.bg-accent {
  background-color: rgba(13, 110, 253, 0.05) !important;
}
.bg-purple {
  background-color: rgba(102, 68, 126, 0.05) !important;
}
</style>

<style>
.ql-editor {
  min-height: 100px !important;
}
.ql-disabled {
  color: rgba(94, 86, 105, 0.38);
}
</style>
